import { useState } from 'react';
import { Button, Modal, cn } from '@cyphex/ui';
import { DEFAULT_QUICK_REACTIONS } from '../../constants';
import { useQuickReactions } from '../../hooks/useQuickReactions';
import { EmojiPicker } from '../composer/EmojiPicker';

interface QuickReactionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Pick the three emoji that appear inline on message hover.
 *
 * Editing happens SLOT BY SLOT rather than as a free-form list, because the
 * hover bar has exactly three places and a list editor would let the user
 * produce a set that cannot be rendered.
 */
export function QuickReactionsDialog({ open, onOpenChange }: QuickReactionsDialogProps) {
  const { emoji, setEmoji } = useQuickReactions();
  const [editing, setEditing] = useState<number | null>(null);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Quick reactions"
      description="These three appear on every message you hover. Click one to change it."
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={() => setEmoji([...DEFAULT_QUICK_REACTIONS])}>
            Reset to defaults
          </Button>
          <Button variant="primary" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </>
      }
    >
      <div className="flex items-center justify-center gap-3 py-2">
        {emoji.map((value, index) => (
          <EmojiPicker
            key={`${value}-${index}`}
            open={editing === index}
            onOpenChange={(next) => setEditing(next ? index : null)}
            onSelect={(picked) => {
              const nextSet = [...emoji];
              nextSet[index] = picked;
              setEmoji(nextSet);
              setEditing(null);
            }}
            trigger={
              <button
                type="button"
                aria-label={`Change quick reaction ${index + 1} (currently ${value})`}
                onClick={() => setEditing(index)}
                className={cn(
                  'flex h-14 w-14 items-center justify-center rounded-xl border border-border bg-card text-3xl',
                  'transition-colors duration-fast hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  editing === index && 'border-primary ring-2 ring-ring',
                )}
              >
                <span aria-hidden>{value}</span>
              </button>
            }
          />
        ))}
      </div>
    </Modal>
  );
}
